"use client";

import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trackEvent } from "@/lib/analytics/track";
import { DEFAULT_LOCALE } from "@/lib/i18n";
import type { PlatformLocale } from "@/lib/i18n/platform";
import type { AttractionId } from "@/lib/attractions/types";
import { takeContactSupportDraft } from "@/lib/contact-support-draft";

const TOPICS = [
  { value: "setup_question", label: "Setup question" },
  { value: "billing_question", label: "Billing question" },
  { value: "route_question", label: "Route or date question" },
  { value: "bug_report", label: "Something broke" },
  { value: "agency_partnership", label: "Agency partnership" },
] as const;

type TopicValue = (typeof TOPICS)[number]["value"];

function cleanDefault(value: string | undefined, maxLength: number): string {
  return (value ?? "").replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function defaultTopicValue(value: string | undefined): TopicValue {
  return TOPICS.some((topic) => topic.value === value) ? (value as TopicValue) : "setup_question";
}

export default function ContactForm({
  defaultEmail,
  defaultTopic,
  defaultMessage,
  locale = DEFAULT_LOCALE,
  attractionId = "machu-picchu",
}: {
  defaultEmail?: string;
  defaultTopic?: string;
  defaultMessage?: string;
  locale?: PlatformLocale;
  attractionId?: AttractionId;
}) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState(() => cleanDefault(defaultEmail, 254));
  const [topic, setTopic] = useState<TopicValue>(() => defaultTopicValue(defaultTopic));
  const [message, setMessage] = useState(() => cleanDefault(defaultMessage, 1000));
  useEffect(() => {
    let draft = null;
    try {
      draft = takeContactSupportDraft(window.sessionStorage);
    } catch {
      return;
    }
    if (!draft) return;
    setEmail((current) => current || cleanDefault(draft.email, 254));
    setTopic("setup_question");
    setMessage((current) => current || cleanDefault(draft.message, 1000));
  }, []);
  const copy =
    locale === "es"
      ? {
          sentTitle: "Mensaje recibido",
          sentBody: "Gracias. Guardamos tu solicitud y usaremos el email que indicaste para responder.",
          name: "Nombre",
          optional: "Opcional",
          topic: "Tema",
          website: "Sitio web",
          message: "Mensaje",
          placeholder: "Dinos que ruta, fecha o paso de pago quieres consultar.",
          sending: "Enviando...",
          submit: "Enviar mensaje",
          topics: {
            setup_question: "Pregunta de configuracion",
            billing_question: "Pregunta de facturacion",
            route_question: "Pregunta de ruta o fecha",
            bug_report: "Algo fallo",
            agency_partnership: "Alianza con agencia",
          } satisfies Record<TopicValue, string>,
        }
      : locale === "pt"
        ? {
          sentTitle: "Mensagem recebida",
          sentBody: "Obrigado. Salvamos sua solicitação e usaremos o email informado para responder.",
          name: "Nome",
          optional: "Opcional",
          topic: "Assunto",
          website: "Site",
          message: "Mensagem",
          placeholder: "Conte qual rota, data ou etapa de pagamento você quer consultar.",
          sending: "Enviando...",
          submit: "Enviar mensagem",
          topics: {
            setup_question: "Pergunta de configuração",
            billing_question: "Pergunta de cobrança",
            route_question: "Pergunta de rota ou data",
            bug_report: "Algo falhou",
            agency_partnership: "Parceria com agência",
          } satisfies Record<TopicValue, string>,
        }
      : locale === "fr"
        ? {
          sentTitle: "Message reçu", sentBody: "Merci. Votre demande est enregistrée et nous répondrons à l’adresse indiquée.", name: "Nom", optional: "Facultatif", topic: "Sujet", website: "Site web", message: "Message", placeholder: "Indiquez l’alerte, la date ou l’étape de paiement concernée.", sending: "Envoi…", submit: "Envoyer",
          topics: { setup_question: "Question de configuration", billing_question: "Question de facturation", route_question: "Question de billet ou de date", bug_report: "Un problème technique", agency_partnership: "Partenariat agence" } satisfies Record<TopicValue, string>,
        }
      : locale === "de"
        ? {
          sentTitle: "Nachricht erhalten", sentBody: "Danke. Ihre Anfrage wurde gespeichert; wir antworten an die angegebene E-Mail-Adresse.", name: "Name", optional: "Optional", topic: "Thema", website: "Website", message: "Nachricht", placeholder: "Nennen Sie die Benachrichtigung, das Datum oder den Zahlungsschritt.", sending: "Wird gesendet…", submit: "Nachricht senden",
          topics: { setup_question: "Frage zur Einrichtung", billing_question: "Frage zur Zahlung", route_question: "Frage zu Ticket oder Datum", bug_report: "Technisches Problem", agency_partnership: "Agenturpartnerschaft" } satisfies Record<TopicValue, string>,
        }
      : locale === "nl"
        ? {
          sentTitle: "Bericht ontvangen", sentBody: "Bedankt. Je verzoek is opgeslagen; we antwoorden via het opgegeven e-mailadres.", name: "Naam", optional: "Optioneel", topic: "Onderwerp", website: "Website", message: "Bericht", placeholder: "Noem de melding, datum of betaalstap waarover je een vraag hebt.", sending: "Versturen…", submit: "Bericht versturen",
          topics: { setup_question: "Vraag over instellen", billing_question: "Vraag over betaling", route_question: "Vraag over ticket of datum", bug_report: "Technisch probleem", agency_partnership: "Samenwerking met bureau" } satisfies Record<TopicValue, string>,
        }
      : {
          sentTitle: "Message received",
          sentBody: "Thanks. We saved your request and will use the email you provided to follow up.",
          name: "Name",
          optional: "Optional",
          topic: "Topic",
          website: "Website",
          message: "Message",
          placeholder: "Tell us which route, date, or checkout step you are asking about.",
          sending: "Sending...",
          submit: "Send message",
          topics: Object.fromEntries(TOPICS.map((topic) => [topic.value, topic.label])) as Record<TopicValue, string>,
        };

  if (sent) {
    return (
      <div className="rounded-lg border border-emerald/20 bg-emerald/10 p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald" />
          <div>
            <h2 className="font-semibold tracking-tight">{copy.sentTitle}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {copy.sentBody}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const data = new FormData(form);
        setBusy(true);
        setError(null);
        try {
          const res = await fetch("/api/contact", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(Object.fromEntries(data.entries())),
          });
          const payload = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
          if (!res.ok || !payload?.ok) throw new Error(payload?.error || `Request failed (${res.status})`);
          trackEvent("contact_submit_success", {
            topic: String(data.get("topic") ?? "setup_question"),
            locale,
          });
          setSent(true);
          form.reset();
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          trackEvent("contact_submit_error", {
            error_message: message.slice(0, 180),
          });
          setError(message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <input type="hidden" name="locale" value={locale} />
      <input type="hidden" name="attractionId" value={attractionId} />

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-1.5">
          <span className="text-sm font-medium">Email</span>
          <Input
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label className="space-y-1.5">
          <span className="text-sm font-medium">{copy.name}</span>
          <Input name="name" autoComplete="name" placeholder={copy.optional} />
        </label>
      </div>

      <label className="space-y-1.5">
        <span className="text-sm font-medium">{copy.topic}</span>
        <select
          name="topic"
          value={topic}
          onChange={(event) => setTopic(defaultTopicValue(event.target.value))}
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {TOPICS.map((topic) => (
            <option key={topic.value} value={topic.value}>
              {copy.topics[topic.value]}
            </option>
          ))}
        </select>
      </label>

      <label className="hidden">
        <span>{copy.website}</span>
        <Input name="website" tabIndex={-1} autoComplete="off" />
      </label>

      <label className="space-y-1.5">
        <span className="text-sm font-medium">{copy.message}</span>
        <textarea
          name="message"
          required
          minLength={20}
          rows={6}
          placeholder={copy.placeholder}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </label>

      {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}

      <Button
        disabled={busy}
        className="bg-emerald text-white font-semibold hover:bg-emerald/90"
      >
        {busy ? copy.sending : copy.submit}
        <ArrowRight className="size-4" />
      </Button>
    </form>
  );
}
